import type { EligibilityRule } from '../types/admissions';
import dateTimeUtils from './dateTimeUtils';

/**
 * Age on a given date, the way schools quote it: completed years, months
 * and days. Mirrors backend/src/utils/age.ts, which judges eligibility —
 * the form shows the parent the same number the server will use. Dates
 * are calendar dates (yyyy-mm-dd or UTC midnight), read in UTC.
 */
export interface Age { years: number; months: number; days: number }

const DAY = 86_400_000;

const monthsAfter = (dob: Date, months: number): number => {
  const y = dob.getUTCFullYear();
  const m = dob.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return Date.UTC(y, m, Math.min(dob.getUTCDate(), lastDay));
};

export function ageOn(dob: Date | string, on: Date | string): Age {
  const b = new Date(dob);
  const o = new Date(on);
  const day = Date.UTC(o.getUTCFullYear(), o.getUTCMonth(), o.getUTCDate());
  let total = (o.getUTCFullYear() - b.getUTCFullYear()) * 12 + o.getUTCMonth() - b.getUTCMonth();
  if (monthsAfter(b, total) > day) total -= 1;
  return { years: Math.floor(total / 12), months: total % 12, days: Math.round((day - monthsAfter(b, total)) / DAY) };
}

export const ageInMonths = (a: Age): number => a.years * 12 + a.months;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** "4 years 3 months 2 days" — an applicant's age. */
export const formatAge = (a: Age): string =>
  [plural(a.years, 'year'), plural(a.months, 'month'), plural(a.days, 'day')].join(' ');

/** "2y 6m", "3y", "8m" — an age limit, compact. */
export function formatMonths(total: number): string {
  const y = Math.floor(total / 12);
  const m = total % 12;
  if (!m) return `${y}y`;
  return y ? `${y}y ${m}m` : `${m}m`;
}

/** "2y 6m – 3y 6m", "At least 2y 6m", "Up to 3y 6m" — null when unlimited. */
export function ageLimitText(min?: number | null, max?: number | null): string | null {
  if (min != null && max != null) return `${formatMonths(min)} – ${formatMonths(max)}`;
  if (min != null) return `At least ${formatMonths(min)}`;
  if (max != null) return `Up to ${formatMonths(max)}`;
  return null;
}

export interface AgeCheck {
  /** "4 years 3 months 2 days" on the cut-off date. */
  age: string;
  /** "1 May 2026". */
  on: string;
  /** The class's limit, e.g. "2y 6m – 3y 6m"; null when the class has none. */
  limit: string | null;
  fits: boolean;
}

/**
 * The applicant's age on their class's cut-off date, and whether it fits
 * the class's age limit. Null until there's a date of birth and a class
 * with a dated age rule.
 */
export function checkAge(
  eligibility: Array<Pick<EligibilityRule, 'classId' | 'ageOn'>> | undefined,
  classId: string | undefined,
  dob: string | Date | undefined | null,
): AgeCheck | null {
  if (!dob || !classId) return null;
  const rule = (eligibility || []).find(r => String(r.classId) === String(classId));
  const cut = rule?.ageOn;
  if (!cut?.date) return null;
  const age = ageOn(dob, cut.date);
  const months = ageInMonths(age);
  if (months < 0) return null;
  const { minMonths: min, maxMonths: max } = cut;
  return {
    age: formatAge(age),
    on: dateTimeUtils.formatDateForDisplay(cut.date),
    limit: ageLimitText(min, max),
    fits: (min == null || months >= min) && (max == null || months <= max),
  };
}

const addMonthsUTC = (d: Date, months: number): Date => {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + months;
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m, Math.min(d.getUTCDate(), lastDay)));
};

/**
 * The birth dates an age limit lets in: "born 2 Nov 2022 – 1 May 2023".
 * Lets an admin check a rule against a date of birth at a glance.
 */
export function birthWindow(on: string | Date, min?: number | null, max?: number | null): string | null {
  const cut = new Date(on);
  if (isNaN(cut.getTime()) || (min == null && max == null)) return null;
  const fmt = (d: Date) => dateTimeUtils.formatDateForDisplay(d, 'UTC');
  const latest = min != null ? fmt(addMonthsUTC(cut, -min)) : null;
  const earliest = max != null ? fmt(new Date(addMonthsUTC(cut, -(max + 1)).getTime() + DAY)) : null;
  if (earliest && latest) return `Born ${earliest} – ${latest}`;
  return earliest ? `Born on or after ${earliest}` : `Born on or before ${latest}`;
}
